import { Button } from "@fluentui/react-components";
import {
	Star20Regular,
	TextEditStyle20Regular,
	Delete20Regular,
	Star20Filled,
} from "@fluentui/react-icons";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { ExtendedMenuItemsProps } from "./types";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { ContactType, Queue } from "@/repository/models";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { Calendar20Regular } from "@fluentui/react-icons";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Flex } from "@/components/atoms/flex";

export const ExtendedMenuItems = (props: ExtendedMenuItemsProps) => {
	const {
		contact,
		showAddToFavorites,
		hideFavoriteOption,
		favoriteContactsIds,
		onFavoriteClick: onToggleFavoriteClick,
		onEditClick,
		onAgendaClick,
		onRemoveClick,
	} = props;
	const intl = useIntl();
	const queueStore = useQueuesStore((state: QueuesState) => state.queues);
	const isContactsAgendaEnabled = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.isContactsAgendaEnabled
	);

	const contactNotesEnabled = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.contactNotesEnabled
	);

	const extendedMenuItems = useMemo<JSX.Element[]>(() => {
		const items: JSX.Element[] = [];
		if (!hideFavoriteOption) {
			items.push(
				<Button
					title={intl.formatMessage({
						id: `Contacts.Menu.${
							showAddToFavorites ? "AddTo" : "RemoveFrom"
						}Favorites`,
					})}
					shape="circular"
					appearance="subtle"
					disabled={
						showAddToFavorites && favoriteContactsIds.includes(contact.id)
					}
					icon={showAddToFavorites ? <Star20Regular /> : <Star20Filled />}
					onClick={() => {
						onToggleFavoriteClick(contact);
					}}
				/>
			);
		}

		const canBeEdited =
			props.isContactEditingAllowed &&
			contact.contactType === ContactType.Database;

		if (!canBeEdited && isContactsAgendaEnabled) {
			items.push(
				<Button
					title={intl.formatMessage({ id: "Contacts.Menu.Agenda" })}
					shape="circular"
					appearance="subtle"
					disabled={contact.contactType !== ContactType.AzureActiveDirectory}
					icon={<Calendar20Regular />}
					onClick={() => onAgendaClick(contact)}
				/>
			);
		}

		const currentUserId = Lockr.get(StorageKeys.UserId);
		const isUserSupervisorOfContactQueues = queueStore.some(
			(q: Queue) => q.isSupervisor && contact.queueIds.includes(q.id)
		);
		const isContactCreator = contact.creatorId === currentUserId;

		const canBeDeleted = contact.isImported
			? isUserSupervisorOfContactQueues
			: canBeEdited && (isContactCreator || isUserSupervisorOfContactQueues);

		if (canBeEdited) {
			items.push(
				<Button
					key="edit"
					title={intl.formatMessage({ id: "Contacts.Menu.EditContact" })}
					shape="circular"
					appearance="subtle"
					icon={<TextEditStyle20Regular />}
					onClick={() => onEditClick(contact)}
				/>
			);
		}

		if (canBeDeleted) {
			items.push(
				<Button
					key="remove"
					title={intl.formatMessage({ id: "Contacts.Menu.RemoveContact" })}
					shape="circular"
					appearance="subtle"
					icon={<Delete20Regular />}
					onClick={() => onRemoveClick(contact)}
				/>
			);
		}

		if (contactNotesEnabled) {
			items.push(props.notesPopover);
		}

		return items;
	}, [
		intl,
		showAddToFavorites,
		hideFavoriteOption,
		contact,
		isContactsAgendaEnabled,
		onAgendaClick,
		onToggleFavoriteClick,
		onEditClick,
		onRemoveClick,
	]);

	if (!extendedMenuItems.length) {
		return <></>;
	}

	return <Flex>{extendedMenuItems}</Flex>;
};
